/**
 * Attendance Page - Simplified
 *
 * Simple flow: Select class → Select/Create date → Mark attendance
 */

import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { SearchableSelect, Spinner } from "../../components/ui";
import type { SearchableSelectItem } from "../../components/ui";
import {
  useCreateLessonSession,
  useDeleteLessonSession,
  useLessonSession,
  useSessionsByClassGroup,
  useUpdateLessonSession,
  useUpdateSessionAttendances,
} from "../../hooks/useAttendance";
import { useTeacherClasses } from "../../hooks/useTeacher";
import { useMyLessons } from "../../hooks/useLessons";
import type { AttendanceStatus } from "../../types/attendance";

// Format date for display (English format)
function formatDate(dateStr: string): string {
  if (!dateStr) return "Invalid date";

  try {
    // Handle both YYYY-MM-DD and date object formats
    let date: Date;

    if (typeof dateStr === "string" && dateStr.includes("-")) {
      // Parse YYYY-MM-DD format from backend
      const [year, month, day] = dateStr.split("-").map(Number);
      if (!year || !month || !day) return "Invalid date";

      // Create date at noon to avoid timezone issues
      date = new Date(year, month - 1, day, 12, 0, 0);
    } else {
      // Try parsing as regular date
      date = new Date(dateStr);
    }

    if (isNaN(date.getTime())) return "Invalid date";

    // Format in English
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "2-digit",
    });
  } catch (error) {
    console.error("Error formatting date:", dateStr, error);
    return "Invalid date";
  }
}

// Parse a YYYY-MM-DD string into a local Date at noon (avoids timezone drift)
function parseSessionDate(dateStr: string): Date | null {
  if (typeof dateStr !== "string" || !dateStr.includes("-")) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0);
}

// Short day label for a session pill (month is shown in the group header)
function formatDay(dateStr: string): string {
  const date = parseSessionDate(dateStr);
  if (!date || isNaN(date.getTime())) return "Invalid date";
  return date.toLocaleDateString("en-US", { weekday: "short", day: "2-digit" });
}

// "June 2026" header label from a YYYY-MM key
function formatMonthLabel(monthKey: string): string {
  const [year, month] = monthKey.split("-").map(Number);
  const date = new Date(year, month - 1, 1, 12, 0, 0);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

interface SessionMonthGroup {
  key: string; // YYYY-MM
  label: string;
  sessions: any[];
}

/**
 * Group sessions strictly by the calendar month of each session's date.
 * Months are ordered chronologically (oldest first) and days are ascending
 * within each month. All months are shown.
 */
function groupSessionsByMonth(sessions: any[]): SessionMonthGroup[] {
  const groups = new Map<string, any[]>();
  for (const session of sessions) {
    const date = parseSessionDate(session.date);
    if (!date) continue;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(session);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => ({
      key,
      label: formatMonthLabel(key),
      sessions: items.sort((a, b) =>
        String(a.date).localeCompare(String(b.date)),
      ),
    }));
}

// Format date for input
function formatDateForInput(date: Date): string {
  return date.toISOString().split("T")[0];
}

// Get today's date string
function getTodayString(): string {
  return formatDateForInput(new Date());
}

export function AttendancePage() {
  // State
  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const [selectedSessionId, setSelectedSessionId] = useState<string>("");
  const [newSessionDate, setNewSessionDate] = useState(getTodayString());
  const [newSessionContent, setNewSessionContent] = useState("");
  const [showNewSession, setShowNewSession] = useState(false);
  const [attendanceState, setAttendanceState] = useState<
    Record<string, AttendanceStatus>
  >({});
  const [contentText, setContentText] = useState("");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">(
    "idle",
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<{
    id: string;
    date: string;
  } | null>(null);

  // Queries
  const { data: classes, isLoading: loadingClasses } = useTeacherClasses();
  const { data: sessions, isLoading: loadingSessions } =
    useSessionsByClassGroup(selectedClassId);
  const { data: sessionDetail, refetch: refetchSession } =
    useLessonSession(selectedSessionId);

  // Extract actual sessions from grouped data
  // The backend returns grouped data: [{id, name, levelCode, teacherName, sessions: []}]
  const actualSessions =
    sessions && sessions.length > 0 && (sessions[0] as any).sessions
      ? (sessions[0] as any).sessions
      : [];

  // Group sessions by month for display (most recent months first)
  const sessionMonthGroups = useMemo(
    () => groupSessionsByMonth(actualSessions),
    [actualSessions],
  );

  // Build searchable options for the class selector
  const classItems = useMemo<SearchableSelectItem[]>(
    () =>
      (classes ?? []).map((cls) => ({
        id: cls.id,
        label: `${cls.name} (${cls.levelCode}) - ${cls.studentCount} students`,
        searchText: `${cls.name} ${cls.levelCode}`,
      })),
    [classes],
  );

  // Debug: log sessions data
  useEffect(() => {
    if (sessions) {
      console.log("Sessions loaded (grouped):", sessions);
      console.log("Actual sessions:", actualSessions);
    }
  }, [sessions, actualSessions]);

  // Debug: log session detail
  useEffect(() => {
    if (sessionDetail) {
      console.log("Session detail loaded:", sessionDetail);
    }
  }, [sessionDetail]);

  // Mutations
  const createSession = useCreateLessonSession();
  const deleteSession = useDeleteLessonSession();
  const updateSession = useUpdateLessonSession();
  const updateAttendances = useUpdateSessionAttendances();

  // Get selected class details
  const selectedClass = classes?.find((c) => c.id === selectedClassId);

  // Lessons of the selected class's level (to show what's being taught)
  const { data: lessonsData } = useMyLessons(selectedClass?.levelCode);

  // Lessons for the month of the selected session (fallback: current month),
  // so the teacher can remember what to cover and jump to the slides.
  const targetMonthDate =
    (sessionDetail?.date ? parseSessionDate(sessionDetail.date) : null) ??
    new Date();
  const targetMonth = targetMonthDate.getMonth() + 1;
  const targetMonthName = targetMonthDate.toLocaleDateString("en-US", {
    month: "long",
  });
  const monthLessons = (lessonsData?.lessons ?? [])
    .filter((l) => l.monthAvailable === targetMonth)
    .sort((a, b) => a.order - b.order);

  // Use session-specific student list when available (respects deactivation dates),
  // otherwise fall back to class group students
  const studentList = useMemo(() => {
    if (sessionDetail?.students?.length) {
      return sessionDetail.students.map((s) => ({
        id: s.id,
        fullName: s.fullName,
        email: s.email,
        phone: "",
        avatar: null as string | null,
      }));
    }
    return selectedClass?.students ?? [];
  }, [sessionDetail, selectedClass]);

  // Initialize attendance state when session loads
  useEffect(() => {
    if (sessionDetail?.attendances) {
      const state: Record<string, AttendanceStatus> = {};
      sessionDetail.attendances.forEach((att) => {
        state[att.student.id] = att.status;
      });
      // Also set default "present" for students without attendance records yet
      studentList.forEach((s) => {
        if (!(s.id in state)) {
          state[s.id] = "present";
        }
      });
      setAttendanceState(state);
      // Also initialize content text
      setContentText(sessionDetail.content || "");
    } else if (studentList.length > 0) {
      // Default all to present for new sessions
      const state: Record<string, AttendanceStatus> = {};
      studentList.forEach((s) => {
        state[s.id] = "present";
      });
      setAttendanceState(state);
      setContentText("");
    }
  }, [sessionDetail, studentList]);

  // Reset session when class changes
  useEffect(() => {
    setSelectedSessionId("");
    setShowNewSession(false);
    setContentText("");
  }, [selectedClassId]);

  // Handle creating new session
  const handleCreateSession = async () => {
    if (!selectedClassId || !newSessionDate) {
      toast.error("Please select a class and date");
      return;
    }

    const toastId = toast.loading("Creating lesson date...");

    try {
      const sessionData: any = {
        classGroup: selectedClassId,
        date: newSessionDate,
      };

      // Only add content if it's not empty
      if (newSessionContent && newSessionContent.trim()) {
        sessionData.content = newSessionContent;
      }

      const result = await createSession.mutateAsync(sessionData);

      toast.success("Lesson date created successfully!", { id: toastId });
      setSelectedSessionId(result.id);
      setShowNewSession(false);
      setNewSessionDate(getTodayString());
      setNewSessionContent("");
    } catch (err: any) {
      console.error("Error creating session:", err);
      console.error("Error response:", err?.response?.data);

      // Get the actual error message
      let errorMsg = "Failed to create lesson date. Please try again.";

      if (err?.response?.data) {
        const data = err.response.data;

        // Check for nonFieldErrors
        if (data.nonFieldErrors && data.nonFieldErrors.length > 0) {
          errorMsg = data.nonFieldErrors[0];
        }
        // Check for other field errors
        else if (data.message) {
          errorMsg = data.message;
        } else if (data.detail) {
          errorMsg = data.detail;
        } else if (data.class_group?.[0]) {
          errorMsg = data.class_group[0];
        } else if (data.date?.[0]) {
          errorMsg = data.date[0];
        } else if (data.content?.[0]) {
          errorMsg = data.content[0];
        }
      }

      toast.error(errorMsg, { id: toastId, duration: 5000 });
    }
  };

  // Handle deleting session
  // Handle deleting session
  const handleDeleteSession = async (
    sessionId: string,
    sessionDate: string,
  ) => {
    setSessionToDelete({ id: sessionId, date: sessionDate });
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!sessionToDelete) return;

    const toastId = toast.loading("Deleting lesson date...");

    try {
      await deleteSession.mutateAsync(sessionToDelete.id);
      toast.success("Lesson date deleted successfully!", { id: toastId });

      // Clear selection if the deleted session was selected
      if (selectedSessionId === sessionToDelete.id) {
        setSelectedSessionId("");
      }

      setShowDeleteModal(false);
      setSessionToDelete(null);
    } catch (err: any) {
      console.error("Error deleting session:", err);
      const errorMsg =
        err?.response?.data?.detail ||
        "Failed to delete lesson date. Please try again.";
      toast.error(errorMsg, { id: toastId, duration: 5000 });
    }
  };

  // Handle status toggle
  const toggleStatus = (studentId: string) => {
    setAttendanceState((prev) => {
      const current = prev[studentId] || "present";
      // Simple toggle: present → absent → present
      const next = current === "present" ? "absent" : "present";
      return { ...prev, [studentId]: next };
    });
  };

  // Handle save all (content + attendance)
  const handleSaveAll = async () => {
    if (!selectedSessionId) return;

    // Validate content is filled
    if (!contentText.trim()) {
      toast.error("Please add lesson content before saving");
      return;
    }

    setSaveStatus("saving");
    const toastId = toast.loading("Saving lesson data...");

    try {
      // First save content
      await updateSession.mutateAsync({
        id: selectedSessionId,
        data: { content: contentText },
      });

      // Then save attendance
      const attendances = Object.entries(attendanceState).map(
        ([studentId, status]) => ({
          studentId,
          status,
        }),
      );

      await updateAttendances.mutateAsync({
        sessionId: selectedSessionId,
        data: { attendances },
      });

      setSaveStatus("saved");
      toast.success(
        `Lesson saved! ${presentCount} present, ${absentCount} absent`,
        { id: toastId },
      );
      refetchSession();
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (err: any) {
      console.error("Error saving:", err);
      const errorMsg =
        err?.response?.data?.detail ||
        "Failed to save lesson. Please try again.";
      toast.error(errorMsg, { id: toastId, duration: 5000 });
      setSaveStatus("idle");
    }
  };

  // Count stats
  const presentCount = Object.values(attendanceState).filter(
    (s) => s === "present",
  ).length;
  const absentCount = Object.values(attendanceState).filter(
    (s) => s === "absent",
  ).length;

  if (loadingClasses) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/teacher"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-3"
          >
            <i className="ri-arrow-left-line"></i>
            Back
          </Link>
          <h1 className="text-xl font-bold text-earth-800">
            Attendance Tracker
          </h1>
          <p className="text-earth-600 mt-1">
            Mark student attendance for your classes
          </p>
        </div>

        {/* Step 1: Select Class */}
        <div className="bg-white rounded-xl border border-earth-200 p-4 mb-4">
          <label className="block text-sm font-medium text-earth-600 mb-2">
            1. Select a class
          </label>
          <SearchableSelect
            items={classItems}
            value={selectedClassId}
            onChange={setSelectedClassId}
            emptyLabel="Choose a class..."
          />
        </div>

        {/* This month's lesson — informative panel for the selected class */}
        {selectedClass && (
          <div className="rounded-xl border-2 border-olive-200 overflow-hidden mb-4 shadow-sm">
            <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-olive-100 to-olive-50 border-b border-olive-200">
              <div className="w-9 h-9 rounded-lg bg-olive-500 flex items-center justify-center shrink-0">
                <i className="ri-book-open-line text-white text-lg"></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-olive-900">
                  {targetMonthName} lesson · {selectedClass.levelCode}
                </p>
                <p className="text-[11px] text-olive-700">
                  What you're teaching this month — open the slides to continue
                  where you stopped.
                </p>
              </div>
            </div>
            <div className="p-3 sm:p-4 bg-white">
              {monthLessons.length > 0 ? (
                <div className="space-y-2">
                  {monthLessons.map((lesson) => {
                    const slidesUrl =
                      lesson.slidesUrl || lesson.resources?.slides || null;
                    const filesUrl =
                      lesson.filesUrl || lesson.resources?.files || null;
                    return (
                      <div
                        key={lesson.id}
                        className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-olive-50/60 border border-olive-100 rounded-lg"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="shrink-0 px-2 py-0.5 bg-olive-200 text-olive-800 rounded text-xs font-bold">
                            {lesson.order === 0 ? "Intro" : `L${lesson.order}`}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-earth-800 truncate">
                              {lesson.topic}
                            </p>
                            {lesson.grammarFocus && (
                              <p className="text-[11px] text-earth-700 truncate">
                                {lesson.grammarFocus}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {slidesUrl && (
                            <a
                              href={slidesUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-earth-700 hover:bg-earth-800 text-white rounded-lg text-xs font-medium transition-colors"
                            >
                              <i className="ri-slideshow-line"></i>
                              Slides
                            </a>
                          )}
                          {filesUrl && (
                            <a
                              href={filesUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-earth-100 hover:bg-earth-200 text-earth-700 rounded-lg text-xs font-medium transition-colors"
                            >
                              <i className="ri-folder-line"></i>
                              Files
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-earth-700">
                  No lesson set for {targetMonthName} in{" "}
                  {selectedClass.levelCode}. Add one in Admin → Lessons.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Select Session (only shows after class is selected) */}
        {selectedClassId && (
          <div className="bg-white rounded-xl border border-earth-200 p-4 mb-4">
            <label className="block text-sm font-medium text-earth-600 mb-2">
              2. Select lesson date
            </label>

            {loadingSessions ? (
              <div className="py-4 text-center">
                <Spinner size="sm" />
              </div>
            ) : (
              <>
                {/* Existing sessions, grouped by month */}
                {sessionMonthGroups.length > 0 && (
                  <div className="space-y-3 mb-3">
                    {sessionMonthGroups.map((group) => (
                      <div key={group.key}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-earth-700 mb-1.5">
                          {group.label}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {group.sessions.map((session: any) => (
                            <div key={session.id} className="relative group">
                              {" "}
                              <button
                                onClick={() => {
                                  setSelectedSessionId(session.id);
                                  setShowNewSession(false);
                                }}
                                className={`px-3 py-2 pr-8 rounded-lg text-sm font-medium transition-all ${
                                  selectedSessionId === session.id
                                    ? "bg-earth-700 text-white"
                                    : "bg-earth-100 text-earth-600 hover:bg-earth-200"
                                }`}
                              >
                                {formatDay(session.date)}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSession(session.id, session.date);
                                }}
                                className={`absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${
                                  selectedSessionId === session.id
                                    ? "text-white hover:bg-red-600"
                                    : "text-earth-500 hover:bg-red-100 hover:text-red-600"
                                }`}
                                title="Delete this lesson date"
                              >
                                <i className="ri-delete-bin-line text-xs"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* New session button/form */}
                {!showNewSession ? (
                  <button
                    onClick={() => setShowNewSession(true)}
                    className="flex items-center gap-2 text-earth-600 hover:text-earth-800 text-sm font-medium"
                  >
                    <i className="ri-add-circle-line"></i>
                    New lesson date
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex gap-2 items-center">
                      <input
                        type="date"
                        value={newSessionDate}
                        onChange={(e) => setNewSessionDate(e.target.value)}
                        className="flex-1 px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-earth-500"
                      />
                      <button
                        onClick={handleCreateSession}
                        disabled={createSession.isPending}
                        className="px-4 py-2 bg-earth-700 text-white rounded-lg text-sm font-medium hover:bg-earth-800 disabled:opacity-50"
                      >
                        {createSession.isPending ? "..." : "Create"}
                      </button>
                      <button
                        onClick={() => {
                          setShowNewSession(false);
                          setNewSessionContent("");
                        }}
                        className="px-3 py-2 text-earth-500 hover:text-earth-700"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 3: Lesson Content (only shows after session is selected) */}
        {selectedSessionId && selectedClass && (
          <div className="bg-white rounded-xl border border-earth-200 p-4 mb-4">
            <label className="block text-sm font-medium text-earth-600 mb-2">
              3. Add lesson content <span className="text-red-500">*</span>
            </label>
            <div className="flex items-start gap-3 bg-sand-50 p-4 rounded-lg border border-earth-100">
              <div className="flex-1">
                <textarea
                  value={contentText}
                  onChange={(e) => setContentText(e.target.value)}
                  placeholder="What did you cover? e.g. 'Slides up to #12, exercises 1-3, stopped at Present Perfect'"
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-earth-500 resize-none bg-white"
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Mark Attendance (only shows after session is selected) */}
        {selectedSessionId && selectedClass && (
          <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
            {/* Header with stats */}
            <div className="p-4 border-b border-earth-100">
              <label className="block text-sm font-medium text-earth-600 mb-3">
                4. Mark attendance
              </label>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-sm text-earth-600">
                    <span className="font-bold text-green-600">
                      {presentCount}
                    </span>{" "}
                    Present
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-sm text-earth-600">
                    <span className="font-bold text-red-600">
                      {absentCount}
                    </span>{" "}
                    Absent
                  </span>
                </div>
              </div>
            </div>

            {/* Student list */}
            <div className="divide-y divide-earth-100">
              {studentList.map((student) => {
                const status = attendanceState[student.id] || "present";
                const isPresent = status === "present";

                return (
                  <button
                    key={student.id}
                    onClick={() => toggleStatus(student.id)}
                    className={`w-full p-4 flex items-center justify-between transition-colors ${
                      isPresent
                        ? "bg-white hover:bg-green-50"
                        : "bg-red-50 hover:bg-red-100"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                          isPresent
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {student.fullName.charAt(0)}
                      </div>
                      <span
                        className={`font-medium ${
                          isPresent ? "text-earth-800" : "text-red-700"
                        }`}
                      >
                        {student.fullName}
                      </span>
                    </div>
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isPresent
                          ? "bg-green-500 text-white"
                          : "bg-red-500 text-white"
                      }`}
                    >
                      <i
                        className={
                          isPresent ? "ri-check-line" : "ri-close-line"
                        }
                      ></i>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Save button */}
            <div className="p-4 border-t border-earth-100">
              <button
                onClick={handleSaveAll}
                disabled={saveStatus === "saving" || !contentText.trim()}
                className={`w-full py-3 rounded-lg font-medium transition-all ${
                  saveStatus === "saved"
                    ? "bg-green-500 text-white"
                    : !contentText.trim()
                      ? "bg-earth-300 text-earth-500 cursor-not-allowed"
                      : "bg-earth-700 text-white hover:bg-earth-800"
                } disabled:opacity-50`}
              >
                {saveStatus === "saving"
                  ? "Saving lesson..."
                  : saveStatus === "saved"
                    ? "✓ Lesson Saved!"
                    : !contentText.trim()
                      ? "Add content first"
                      : "Save Lesson"}
              </button>
              {!contentText.trim() && (
                <p className="text-xs text-center text-red-500 mt-2">
                  Please add lesson content before saving
                </p>
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!selectedClassId && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-earth-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-checkbox-circle-line text-2xl text-earth-400"></i>
            </div>
            <p className="text-earth-700">Select a class to get started</p>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && sessionToDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-2xl text-red-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-earth-800 mb-2">
                    Delete Lesson Date?
                  </h3>
                  <p className="text-earth-600 mb-1">
                    Are you sure you want to delete the lesson date for{" "}
                    <span className="font-semibold">
                      {formatDate(sessionToDelete.date)}
                    </span>
                    ?
                  </p>
                  <p className="text-sm text-red-600">
                    All attendance records will be permanently lost.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSessionToDelete(null);
                  }}
                  className="flex-1 px-4 py-2.5 bg-earth-100 text-earth-700 rounded-lg font-medium hover:bg-earth-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AttendancePage;
